<template>
  <div class="text-per-date-widget" :style="widgetStyle">
    <div class="text-per-date-widget__dp-wrapper h-v-centerer">
      <div class="text-per-date-widget__date-picker v-centerer">
        <Icon
          :w="12"
          class="text-per-date-widget__date-picker__arrow text-per-date-widget__date-picker__arrow--left"
          @click.native="prevDate"
        >
          {{ wideArrowRightMedium }}
        </Icon>
        <span class="text-per-date-widget__date-picker__date">
          {{ displayedCurrentDate }}
        </span>
        <Icon
          :w="12"
          class="text-per-date-widget__date-picker__arrow text-per-date-widget__date-picker__arrow--right"
          @click.native="nextDate"
        >
          {{ wideArrowRightMedium }}
        </Icon>
      </div>
    </div>

    <div class="text-per-date-widget__text-wrapper">
      <EditableLabel
        :value="currentDateText"
        use-tiptap-editor
        display-tiptap-menu
        display-tiptap-tasks
        :textarea-edition-timeout="0"
        @input="setTextAtCurrentDate"
      />

      <LoadingOverlay :value="isRefreshing" />
    </div>
  </div>
</template>

<script>
/**
 * Text per date widget.
 * Allows the user to use a text editor to enter any text at a given date.
 */

import axios from 'axios';
// Vuex utils
import { mapActions } from 'vuex';
import { FETCH_TEXT_PER_DATE_WIDGET, SET_TEXT_PER_DATE_WIDGET } from '@/store/action-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import { isColorDark, PERIOD_UNITS_DATA } from '@/utils/main';
import SocketMessages from '@/utils/classes/SocketMessages';
import { wideArrowRightMedium } from '@/assets/icons';

export default {

  name: 'TextPerDateWidget',

  components: {
    EditableLabel,
    Icon,
    LoadingOverlay,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinSocketHandler],

  props: {
    textPerDateDefaultText: {
      type: String,
      default: '',
    },
    textPerDatePeriodIdx: {
      type: Number,
      default: PERIOD_UNITS_DATA.findIndex(item => item.letter === 'd'),
    },
  },

  data() {
    return {
      wideArrowRightMedium,
      isRefreshing: false,
      fieldKey: 1,
      displayDateFormat: this.$t('fullDateFormat'),
      // Creating cancelTokens for axios requests
      saveRequestCancel: null,
    };
  },

  computed: {
    config() {
      return [
        {
          key: 'tpdTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'textPerDateDefaultText',
          value: this.textPerDateDefaultText,
          component: 'EditableLabel',
          options: {
            useTiptapEditor: true,
            displayTiptapMenu: true,
            displayTiptapTasks: true,
            legend: this.$t('message.WIDGET_TEXT_CONTENT'),
            class: 'mt5em',
          },
        },
        {
          key: 'textPerDatePeriodIdx',
          value: this.textPerDatePeriodIdx,
          component: 'EditableLabelRadio',
          options: {
            legend: this.$t('message.PERIOD'),
            class: 'mt10em text-per-date-widget__period',
            items: this.periodItems,
            labelKey: 'unit',
            genericBorder: true,
          },
        },
        {
          key: 'textPerDatePeriodIdxHint',
          value: '',
          component: 'Div',
          options: {
            class: 'field-hint text-per-date-widget__hint',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.WIDGET_TEXT_PER_DATE_PERIOD_HINT'),
          }],
        },
        this.defaultBgColorConfig,
      ];
    },
    /** Moment: Currently selected date */
    currentDate: {
      get() {
        const { currentDate } = this.queryParams;
        const mDate = this.$moment(currentDate);
        return mDate.isValid() ? mDate : this.$moment();
      },
      set(newDate) {
        this.setQueryParams({ currentDate: newDate.format('YYYY-MM-DD') });
      },
    },
    /** String: Currently selected date formatted */
    displayedCurrentDate() {
      const df = this.displayDateFormat;
      if (this.textPerDatePeriod === 'd') {
        return this.currentDate.format(df);
      }
      return `${this.datesPeriod.start.format(df)} - ${this.datesPeriod.end.format(df)}`;
    },
    /** Object: With a 'start' and 'end' moment values */
    datesPeriod() {
      // Always use monday for start of week
      const unit = this.textPerDatePeriod === 'w' ? 'isoWeek' : this.textPerDatePeriod;
      return {
        start: this.$moment(this.currentDate).startOf(unit),
        end:this.$moment(this.currentDate).endOf(unit),
      };
    },
    /** String: Date key of the textPErDate object */
    dateKey() {
      return this.datesPeriod.start.format('YYYY-MM-DD');
    },
    /** String: Text of the current date. Can be a JSON-encoded string to comply with TipTap */
    currentDateText() {
      // Next line allows detecting reactivity which would otherwise be utterly complex
      // eslint-disable-next-line no-unused-expressions
      this.fieldKey;
      const { textPerDate = {} } = this.$store.state.cfgPagesWidgets[`${this.widget.id}`];
      return textPerDate[this.dateKey] ? textPerDate[this.dateKey].text : this.textPerDateDefaultText;
    },
    /** Object: CSS style of the widget */
    widgetStyle() {
      return {
        '--font-color': isColorDark(this.designOptions.bgColor) ? '#fff' : undefined,
      };
    },
    /** Array: Available units in Shizen */
    periodItems() {
      return PERIOD_UNITS_DATA.map(unit => ({ ...unit, unit: this.$tc(unit.unitTr, 1) }));
    },
    /** String: Moment unit selected as the display period */
    textPerDatePeriod() {
      return this.periodItems[this.textPerDatePeriodIdx].letter;
    },
  },

  watch: {
    currentDate() {
      this.refresh();
    },
  },

  mounted() {
    this.refresh();
    this.handleSocketMessage(SocketMessages.CC_MSGS.TEXT_PER_DATE_WIDGET, (msg) => {
      if (msg.id === this.widget.id && msg.date === this.dateKey) {
        this.refresh();
      }
    });
  },

  methods: {
    /** Refresh the content for the currently selected date */
    refresh() {
      this.isRefreshing = true;
      this.FETCH_TEXT_PER_DATE_WIDGET({ widgetId: this.widget.id, date: this.dateKey })
        .then(() => {
          this.fieldKey++;
          this.isRefreshing = false;
        })
        .catch(() => {
          this.isRefreshing = false;
        });
    },
    /** Set the content for the currently selected date */
    setTextAtCurrentDate(text) {
      // Cancel any previous ongoing XHR
      if (this.saveRequestCancel instanceof Function) {
        this.saveRequestCancel();
      }
      const params = {
        widgetId: this.widget.id,
        date: this.dateKey,
        text,
        cancelToken: new axios.CancelToken((cancel) => {
          this.saveRequestCancel = cancel;
        }),
      };
      this.SET_TEXT_PER_DATE_WIDGET(params)
        .then(() => {
          this.fieldKey++;
          this.sendSocketMessage({
            msg: SocketMessages.CC_MSGS.TEXT_PER_DATE_WIDGET,
            id: this.widget.id,
            date: this.dateKey,
          });
          this.isRefreshing = false;
        })
        .catch(() => {
          this.isRefreshing = false;
        });
    },
    /** Jump to the previous day */
    prevDate() {
      this.currentDate = this.$moment(this.datesPeriod.start).subtract(1, 'd');
    },
    /** Jump to the next day */
    nextDate() {
      this.currentDate = this.$moment(this.datesPeriod.end).add(1, 'd');
    },
    sizeChanged() {
      // Reduce the date format if it overflows
      if (this.$el) {
        this.displayDateFormat = this.$t('fullDateFormat');
        this.$nextTick(() => {
          if (this.$el.offsetWidth < this.$el.scrollWidth) {
            this.displayDateFormat = this.$t('monthDateFormat');
          }
        });
      }
    },
    ...mapActions({
      FETCH_TEXT_PER_DATE_WIDGET,
      SET_TEXT_PER_DATE_WIDGET,
    }),
  },
};

</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.text-per-date-widget {
  display: flex;
  flex-flow: column;
  color: var(--font-color);
  overflow: auto;

  &__date-picker {
    flex: 0 0 auto;

    &__arrow {
      height: 1.4em;
      width: auto;
      padding: 0.5em;
      cursor: pointer;

      &--left {
        transform: rotate(180deg);
      }

      &:hover {
        background: $color-hoverbtn-light-blue;
      }
    }

    &__date {
      padding: 0.2em 0.5em;

      &:hover {
        background: $color-hoverbtn-light-blue;
      }
    }
  }

  &__text-wrapper {
    flex: 1;
    position: relative;

    .text-field-menu-item {
      color: var(--font-color);
    }
  }

  &__period {
    margin-top: 1.4rem;
  }

  &__hint {
    margin-top: 0.8rem;
  }
}
</style>
